import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Checkbox,
  FormControlLabel,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  FormGroup,
  Collapse,
  IconButton,
  Button
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import CategoryIcon from '@mui/icons-material/Category';
import FolderIcon from '@mui/icons-material/Folder';
import ExtensionIcon from '@mui/icons-material/Extension';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import { WizardDialog } from '../WizardDialog';
import { WizardStep } from '../WizardStep';
import { useWizard } from '../../contexts/WizardContext';
import { ContentPreviewPanel, ContentItem } from '../wizard-panels/ContentPreviewPanel';
import { ProgressSummaryPanel, SummarySection } from '../wizard-panels/ProgressSummaryPanel';
import { CreateEnvironmentInline } from '../wizard-panels/CreateEnvironmentInline';
import { moodleApi, MoodleSource, MoodleCourse, DiscoveredScenario, CourseScenarioDiscoveryResult } from '../../services/moodle-api';
import { basePrefixApi } from '../../services/api';
import { useJobs } from '../../contexts/JobsContext';
import ScienceIcon from '@mui/icons-material/Science';

export interface BackupMoodleToS3WizardProps {
  open: boolean;
  onClose: () => void;
  onComplete?: (jobId: string) => void;
}

// Build dynamic wizard steps based on content type selection
const buildWizardSteps = (contentTypes: string[]) => {
  const steps = [
    { label: 'Select Source', description: 'Choose Moodle instance' },
    { label: 'Select Content Types', description: 'Choose what to backup' },
  ];

  if (contentTypes.includes('categories')) {
    steps.push({ label: 'Confirm Categories', description: 'All categories will be backed up' });
  }

  if (contentTypes.includes('courses')) {
    steps.push({ label: 'Select Courses', description: 'Choose courses' });
    steps.push({ label: 'CMI5 Activities & Scenarios', description: 'Review activities and select scenarios' });
  }

  if (contentTypes.includes('frameworks')) {
    steps.push({ label: 'Select Frameworks', description: 'Choose frameworks' });
  }

  if (contentTypes.includes('learning-paths')) {
    steps.push({ label: 'Select Learning Paths', description: 'Choose learning path templates' });
  }

  if (contentTypes.includes('cmi5')) {
    steps.push({ label: 'Select CMI5 Activities', description: 'Choose CMI5 activities to export' });
  }

  steps.push(
    { label: 'Select S3 Environment', description: 'Choose destination' },
    { label: 'Configure Backup', description: 'Set backup options' },
    { label: 'Review & Confirm', description: 'Review and start backup' }
  );

  return steps;
};

// TODO: Refactor to use dynamic steps based on content type selection
// This would create separate steps like:
// - Select Content Types
// - Select Courses (if courses selected)
// - Select Scenarios (if scenarios found)
// - Select Frameworks (if frameworks selected)
// - Select Learning Paths (if learning paths selected)
// This requires significant refactoring of state management and step indices.

// Step 1: Select Source Moodle Instance
const SourceSelectionStep: React.FC = () => {
  const wizard = useWizard();
  const [sources, setSources] = useState<MoodleSource[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSources = async () => {
      try {
        const sourcesData = await moodleApi.listSources();
        setSources(sourcesData);
      } catch (err: any) {
        wizard.setError(`Failed to load Moodle sources: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    loadSources();
  }, []);

  useEffect(() => {
    const selectedSourceId = wizard.getData('sourceId');
    const isValid = !!selectedSourceId && sources.some(s => s.id === selectedSourceId);
    wizard.setStepValidation(0, isValid);
  }, [wizard.data, sources]);

  const handleSourceChange = (sourceId: string) => {
    wizard.setData('sourceId', sourceId);
    const source = sources.find(s => s.id === sourceId);
    wizard.setData('sourceName', source?.name);
    // Clear downstream selections
    wizard.setData('contentTypes', []);
    wizard.setData('selectedCourses', []);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <FormControl fullWidth>
        <InputLabel>Source Moodle Instance</InputLabel>
        <Select
          value={wizard.getData('sourceId', '')}
          onChange={(e) => handleSourceChange(e.target.value)}
          label="Source Moodle Instance"
        >
          {sources.map(source => (
            <MenuItem key={source.id} value={source.id}>
              <Box>
                <Typography variant="body1">{source.name}</Typography>
                {source.description && (
                  <Typography variant="caption" color="text.secondary">
                    {source.description}
                  </Typography>
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {sources.length === 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          No Moodle instances configured. Please add a Moodle instance in the Config section first.
        </Alert>
      )}

      {wizard.getData('sourceId') && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Selected source: <strong>{wizard.getData('sourceName')}</strong>
        </Alert>
      )}
    </Box>
  );
};

// Step 2: Select Content Types Only (first step of content selection)
const ContentTypeSelectionStep: React.FC = () => {
  const wizard = useWizard();
  const [contentTypes, setContentTypes] = useState<string[]>(
    wizard.getData('contentTypes', [])
  );

  // Sync local state with wizard data
  useEffect(() => {
    const types = wizard.getData<string[]>('contentTypes', []);
    setContentTypes(types);
  }, [wizard.data]);

  const toggleContentType = (type: string) => {
    const current = wizard.getData<string[]>('contentTypes', []);
    let newTypes: string[];
    if (current.includes(type)) {
      newTypes = current.filter(t => t !== type);
    } else {
      newTypes = [...current, type];
    }
    wizard.setData('contentTypes', newTypes);
    // Immediately validate after updating data (step 1 = content types)
    wizard.setStepValidation(1, newTypes.length > 0);
  };

  // Initial validation on mount
  useEffect(() => {
    const types = wizard.getData<string[]>('contentTypes', []);
    wizard.setStepValidation(1, types.length > 0);
  }, []);

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom fontWeight="bold">
        Select Content Types to Backup
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose which types of content you want to backup. You'll select specific items in the next steps.
      </Typography>

      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={contentTypes.includes('courses')}
              onChange={() => toggleContentType('courses')}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SchoolIcon />
              <Box>
                <Typography variant="body1">Courses</Typography>
                <Typography variant="caption" color="text.secondary">
                  Moodle course content and activities
                </Typography>
              </Box>
            </Box>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={contentTypes.includes('categories')}
              onChange={() => toggleContentType('categories')}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FolderIcon />
              <Box>
                <Typography variant="body1">Categories</Typography>
                <Typography variant="caption" color="text.secondary">
                  Course categories and hierarchy
                </Typography>
              </Box>
            </Box>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={contentTypes.includes('frameworks')}
              onChange={() => toggleContentType('frameworks')}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ExtensionIcon />
              <Box>
                <Typography variant="body1">Competency Frameworks</Typography>
                <Typography variant="caption" color="text.secondary">
                  Skills and competency definitions
                </Typography>
              </Box>
            </Box>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={contentTypes.includes('learning-paths')}
              onChange={() => toggleContentType('learning-paths')}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CategoryIcon />
              <Box>
                <Typography variant="body1">Learning Path Templates</Typography>
                <Typography variant="caption" color="text.secondary">
                  Structured learning journeys
                </Typography>
              </Box>
            </Box>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={contentTypes.includes('cmi5')}
              onChange={() => toggleContentType('cmi5')}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScienceIcon />
              <Box>
                <Typography variant="body1">CMI5 Activities</Typography>
                <Typography variant="caption" color="text.secondary">
                  Standalone CMI5 activity packages
                </Typography>
              </Box>
            </Box>
          }
        />
      </FormGroup>

      {contentTypes.length > 0 && (
        <Alert severity="info" sx={{ mt: 3 }}>
          You've selected {contentTypes.length} content type(s). Click Next to choose specific items.
        </Alert>
      )}
    </Box>
  );
};

// Step: Confirm Categories (dynamic step - shown if categories content type is selected)
const CategoriesConfirmationStep: React.FC = () => {
  const wizard = useWizard();

  useEffect(() => {
    // Calculate this step's index
    const contentTypes = wizard.getData<string[]>('contentTypes', []);
    let stepIndex = 2; // Start after Source=0, ContentTypes=1
    // Categories is always at position 2 when it's selected
    // Categories step is always valid since we export all categories
    wizard.setStepValidation(stepIndex, true);
  }, [wizard.data]);

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom fontWeight="bold">
        All Categories Will Be Backed Up
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        This will export all course categories and their hierarchical structure from your Moodle instance.
      </Typography>

      <Alert severity="info">
        <Typography variant="body2">
          <strong>What will be backed up:</strong>
        </Typography>
        <Typography variant="body2" component="div" sx={{ mt: 1, ml: 2 }}>
          • All category names<br />
          • Category descriptions<br />
          • Category hierarchy and parent-child relationships<br />
          • Category metadata
        </Typography>
      </Alert>

      <Alert severity="warning" sx={{ mt: 2 }}>
        <Typography variant="body2">
          Note: This backs up the category structure only. Course content is backed up separately when you select the "Courses" option.
        </Typography>
      </Alert>
    </Box>
  );
};

// Step: Select Courses (dynamic step - shown if courses content type is selected)
const CourseSelectionStep: React.FC = () => {
  const wizard = useWizard();
  const [courses, setCourses] = useState<MoodleCourse[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [selectedCourseIds, setSelectedCourseIds] = useState<number[]>(
    wizard.getData('selectedCourseIds', [])
  );
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Build hierarchical category tree from path strings
  interface CategoryNode {
    name: string;
    fullPath: string;
    courses: MoodleCourse[];
    children: CategoryNode[];
  }

  const categoryTree = useMemo(() => {
    const root: CategoryNode = { name: '', fullPath: '', courses: [], children: [] };

    courses.forEach(course => {
      const categoryPath = (course.category as any) || 'Uncategorized';
      const parts = categoryPath.split(' / ');

      let currentNode = root;
      let currentPath = '';

      parts.forEach((part, index) => {
        currentPath = index === 0 ? part : `${currentPath} / ${part}`;

        let childNode = currentNode.children.find(c => c.name === part);
        if (!childNode) {
          childNode = { name: part, fullPath: currentPath, courses: [], children: [] };
          currentNode.children.push(childNode);
        }

        // If this is the last part, add the course here
        if (index === parts.length - 1) {
          childNode.courses.push(course);
        }

        currentNode = childNode;
      });
    });

    return root.children;
  }, [courses]);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  // Get all courses in a category node and its children recursively
  const getAllCoursesInNode = (node: CategoryNode): MoodleCourse[] => {
    const courses = [...node.courses];
    node.children.forEach(child => {
      courses.push(...getAllCoursesInNode(child));
    });
    return courses;
  };

  const toggleCourse = (courseId: number) => {
    setSelectedCourseIds(prev =>
      prev.includes(courseId)
        ? prev.filter(id => id !== courseId)
        : [...prev, courseId]
    );
  };

  const toggleAllCourses = () => {
    if (selectedCourseIds.length === courses.length) {
      setSelectedCourseIds([]);
    } else {
      setSelectedCourseIds(courses.map(c => c.id));
    }
  };

  // Render category tree recursively
  const renderCategoryNode = (node: CategoryNode, level: number = 0): React.ReactNode => {
    const isExpanded = expandedCategories.has(node.fullPath);
    const allCoursesInNode = getAllCoursesInNode(node);
    const categorySelected = allCoursesInNode.length > 0 && allCoursesInNode.every(c => selectedCourseIds.includes(c.id));
    const categorySomeSelected = allCoursesInNode.some(c => selectedCourseIds.includes(c.id)) && !categorySelected;

    return (
      <React.Fragment key={node.fullPath}>
        {/* Category Header */}
        <ListItem
          sx={{
            bgcolor: level === 0 ? 'action.hover' : 'transparent',
            fontWeight: level === 0 ? 'bold' : 'normal',
            borderBottom: level === 0 ? 1 : 0,
            borderColor: 'divider',
            pl: 2 + level * 2
          }}
        >
          <ListItemIcon>
            <IconButton
              size="small"
              onClick={() => toggleCategory(node.fullPath)}
            >
              {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </IconButton>
          </ListItemIcon>
          <ListItemIcon>
            <Checkbox
              checked={categorySelected}
              indeterminate={categorySomeSelected}
              onChange={() => {
                if (categorySelected) {
                  // Deselect all in category
                  setSelectedCourseIds(prev =>
                    prev.filter(id => !allCoursesInNode.some(c => c.id === id))
                  );
                } else {
                  // Select all in category
                  setSelectedCourseIds(prev => {
                    const newIds = allCoursesInNode.map(c => c.id).filter(id => !prev.includes(id));
                    return [...prev, ...newIds];
                  });
                }
              }}
            />
          </ListItemIcon>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CategoryIcon fontSize="small" />
                <Typography variant="body2" fontWeight={level === 0 ? 'bold' : 'normal'}>
                  {node.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ({allCoursesInNode.length} course{allCoursesInNode.length !== 1 ? 's' : ''})
                </Typography>
              </Box>
            }
          />
        </ListItem>

        {/* Courses and subcategories */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {/* Render child categories */}
          {node.children.map(child => renderCategoryNode(child, level + 1))}

          {/* Render courses directly in this category */}
          {node.courses.map(course => (
            <ListItem
              key={course.id}
              button
              onClick={() => toggleCourse(course.id)}
              sx={{ pl: 8 + level * 2 }}
            >
              <ListItemIcon>
                <Checkbox checked={selectedCourseIds.includes(course.id)} />
              </ListItemIcon>
              <ListItemText
                primary={course.fullname}
                secondary={`Short name: ${course.shortname}`}
              />
            </ListItem>
          ))}
        </Collapse>
      </React.Fragment>
    );
  };

  // Load courses when component mounts
  useEffect(() => {
    const sourceId = wizard.getData('sourceId');
    if (!sourceId) return;

    const loadCourses = async () => {
      setLoadingCourses(true);
      try {
        const coursesData = await moodleApi.listCourses(sourceId);
        setCourses(coursesData);
      } catch (err: any) {
        wizard.setError(`Failed to load courses: ${err.message}`);
      } finally {
        setLoadingCourses(false);
      }
    };
    loadCourses();
  }, []);

  // Update wizard data when selection changes
  useEffect(() => {
    wizard.setData('selectedCourseIds', selectedCourseIds);
    wizard.setData('selectedCourses', courses.filter(c => selectedCourseIds.includes(c.id)));
  }, [selectedCourseIds, courses]);

  // Update validation when wizard data changes
  useEffect(() => {
    const courseIds = wizard.getData<number[]>('selectedCourseIds', []);
    // Step is valid if at least one course is selected
    // CourseSelectionStep is always at index 2 (after Source=0, ContentTypes=1)
    wizard.setStepValidation(2, courseIds.length > 0);
  }, [wizard.data]);

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom fontWeight="bold">
        Select Courses to Backup
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose which courses you want to backup. You can select individual courses or entire categories.
      </Typography>

      {loadingCourses ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={40} />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedCourseIds.length === courses.length && courses.length > 0}
                  indeterminate={selectedCourseIds.length > 0 && selectedCourseIds.length < courses.length}
                  onChange={toggleAllCourses}
                />
              }
              label="Select All Courses"
            />
            <Typography variant="body2" color="text.secondary">
              {selectedCourseIds.length} of {courses.length} selected
            </Typography>
          </Box>

          <Paper variant="outlined" sx={{ maxHeight: 500, overflow: 'auto' }}>
            <List dense>
              {categoryTree.map(node => renderCategoryNode(node, 0))}
            </List>
          </Paper>

          {selectedCourseIds.length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Please select at least one course to continue.
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};

// Step 4: Select Scenarios (dynamic step - shown if scenarios are discovered for selected courses)
const ScenarioSelectionStep: React.FC = () => {
  const wizard = useWizard();
  const selectedCourseIds = wizard.getData<number[]>('selectedCourseIds', []);
  const selectedCourses = wizard.getData<MoodleCourse[]>('selectedCourses', []);
  const [courseScenarios, setCourseScenarios] = useState<Map<number, CourseScenarioDiscoveryResult>>(new Map());
  const [loadingScenarios, setLoadingScenarios] = useState<Map<number, boolean>>(new Map());
  const [selectedScenarioUuids, setSelectedScenarioUuids] = useState<Map<number, string[]>>(
    wizard.getData('selectedScenarioUuids', new Map())
  );
  const [skipScenarioDiscovery, setSkipScenarioDiscovery] = useState<boolean>(
    wizard.getData('skipScenarioDiscovery', false)
  );

  // Discover scenarios for selected courses
  const discoverScenariosForCourses = async (courseIds: number[], forceRefresh: boolean = false) => {
    const sourceId = wizard.getData('sourceId');
    if (!sourceId) return;

    for (const courseId of courseIds) {
      // Skip if already loading
      if (loadingScenarios.get(courseId)) {
        continue;
      }

      // Skip if already loaded and not forcing refresh
      if (!forceRefresh && courseScenarios.has(courseId)) {
        continue;
      }

      // Mark as loading
      setLoadingScenarios(prev => new Map(prev).set(courseId, true));

      try {
        const discovery = await moodleApi.discoverScenariosForCourse(sourceId, courseId, forceRefresh);
        setCourseScenarios(prev => new Map(prev).set(courseId, discovery));
      } catch (err: any) {
        console.error(`Failed to discover scenarios for course ${courseId}:`, err);
        // Continue with other courses
      } finally {
        setLoadingScenarios(prev => {
          const newMap = new Map(prev);
          newMap.delete(courseId);
          return newMap;
        });
      }
    }
  };

  const toggleScenario = (courseId: number, scenarioUuid: string) => {
    setSelectedScenarioUuids(prev => {
      const newMap = new Map(prev);
      const courseScenariosList = newMap.get(courseId) || [];

      if (courseScenariosList.includes(scenarioUuid)) {
        // Remove scenario
        const filtered = courseScenariosList.filter(uuid => uuid !== scenarioUuid);
        if (filtered.length === 0) {
          newMap.delete(courseId);
        } else {
          newMap.set(courseId, filtered);
        }
      } else {
        // Add scenario
        newMap.set(courseId, [...courseScenariosList, scenarioUuid]);
      }

      return newMap;
    });
  };

  const toggleAllScenariosForCourse = (courseId: number, scenarios: DiscoveredScenario[]) => {
    setSelectedScenarioUuids(prev => {
      const newMap = new Map(prev);
      const courseScenariosList = newMap.get(courseId) || [];
      const allSelected = scenarios.every(s => courseScenariosList.includes(s.uuid));

      if (allSelected) {
        // Deselect all
        newMap.delete(courseId);
      } else {
        // Select all
        newMap.set(courseId, scenarios.map(s => s.uuid));
      }

      return newMap;
    });
  };

  // Count total scenarios discovered and selected
  const totalScenariosDiscovered = useMemo(() => {
    let count = 0;
    courseScenarios.forEach(discovery => {
      count += discovery.scenarios.length;
    });
    return count;
  }, [courseScenarios]);

  const totalScenariosSelected = useMemo(() => {
    let count = 0;
    selectedScenarioUuids.forEach(uuids => {
      count += uuids.length;
    });
    return count;
  }, [selectedScenarioUuids]);

  const isScanningScenarios = useMemo(() => {
    return Array.from(loadingScenarios.values()).some(loading => loading);
  }, [loadingScenarios]);

  // Discover scenarios on mount (unless skipped)
  useEffect(() => {
    if (selectedCourseIds.length > 0 && !skipScenarioDiscovery) {
      discoverScenariosForCourses(selectedCourseIds, false);
    }
  }, []);

  // Update wizard data when selection changes
  useEffect(() => {
    wizard.setData('selectedScenarioUuids', selectedScenarioUuids);
  }, [selectedScenarioUuids]);

  // Update wizard data when skip state changes
  useEffect(() => {
    wizard.setData('skipScenarioDiscovery', skipScenarioDiscovery);
  }, [skipScenarioDiscovery]);

  // Update validation when wizard data or scanning state changes
  useEffect(() => {
    // Step is always valid - scenarios are optional
    // But we disable "Next" while scanning to ensure users see the scenarios (unless skipped)
    // ScenarioSelectionStep is always at index 3 (Source=0, ContentTypes=1, Courses=2, Scenarios=3)
    wizard.setStepValidation(3, skipScenarioDiscovery || !isScanningScenarios);
  }, [wizard.data, isScanningScenarios, skipScenarioDiscovery]);

  const handleSkipChange = (checked: boolean) => {
    setSkipScenarioDiscovery(checked);
    if (checked) {
      // Clear any selected scenarios when skipping
      setSelectedScenarioUuids(new Map());
    } else if (selectedCourseIds.length > 0 && courseScenarios.size === 0) {
      // Start discovery if we have courses but haven't discovered yet
      discoverScenariosForCourses(selectedCourseIds, false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom fontWeight="bold">
        CMI5 Activities & RangeOS Scenarios
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Review CMI5 activities and their Assignable Units (AUs) for each course. Optionally select RangeOS scenarios to include in the export.
      </Typography>

      {/* Skip option */}
      <Alert
        severity="info"
        sx={{ mb: 2 }}
        action={
          <FormControlLabel
            control={
              <Checkbox
                checked={skipScenarioDiscovery}
                onChange={(e) => handleSkipChange(e.target.checked)}
                size="small"
              />
            }
            label="Skip"
            sx={{ mr: 1 }}
          />
        }
      >
        <Typography variant="body2">
          <strong>Skip scenario discovery?</strong> Scanning {selectedCourseIds.length} course(s) for linked scenarios can take time.
          Check "Skip" to proceed without scanning.
        </Typography>
      </Alert>

      {/* Show rest of UI only if not skipping */}
      {skipScenarioDiscovery ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Scenario discovery skipped. No RangeOS scenarios will be included in this backup.
          </Typography>
        </Alert>
      ) : (
        <>
          {/* Status message */}
          {!isScanningScenarios && totalScenariosDiscovered > 0 && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                {totalScenariosDiscovered} RangeOS scenario(s) discovered!
              </Typography>
              <Typography variant="caption">
                {totalScenariosSelected > 0
                  ? `You have selected ${totalScenariosSelected} scenario(s) to include in your export.`
                  : 'Select scenarios below to include them in your export, or click Next to skip.'}
              </Typography>
            </Alert>
          )}

          {/* Scanning alert */}
          {isScanningScenarios && (
            <Alert severity="warning" sx={{ mb: 2 }} icon={<CircularProgress size={20} />}>
              <Typography variant="body2" fontWeight="bold">
                Scanning courses for draft linkages...
              </Typography>
              <Typography variant="caption">
                Please wait for scanning to complete, or check "Skip" above to proceed without scenarios.
              </Typography>
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => discoverScenariosForCourses(selectedCourseIds, true)}
              disabled={isScanningScenarios}
            >
              Refresh All
            </Button>
          </Box>

          <Paper variant="outlined" sx={{ maxHeight: 500, overflow: 'auto' }}>
        <List dense>
          {selectedCourseIds.map(courseId => {
            const discovery = courseScenarios.get(courseId);
            const course = selectedCourses.find(c => c.id === courseId);
            const isLoading = loadingScenarios.get(courseId);

            // Show loading indicator for courses being scanned
            if (isLoading) {
              return (
                <ListItem
                  key={courseId}
                  sx={{
                    bgcolor: 'action.hover',
                    borderBottom: 1,
                    borderColor: 'divider',
                    py: 1
                  }}
                >
                  <ListItemIcon>
                    <CircularProgress size={20} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SchoolIcon fontSize="small" />
                        <Typography variant="body2">
                          {course?.fullname || `Course ${courseId}`}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="primary">
                        Looking for draft linkages...
                      </Typography>
                    }
                  />
                </ListItem>
              );
            }

            if (!discovery || (discovery.scenarios.length === 0 && (discovery.cmi5Activities || []).length === 0)) {
              return null;
            }

            const courseSelectedScenarios = selectedScenarioUuids.get(courseId) || [];
            const allSelected = discovery.scenarios.length > 0 && discovery.scenarios.every(s => courseSelectedScenarios.includes(s.uuid));
            const someSelected = courseSelectedScenarios.length > 0 && !allSelected;

            // Build a lookup: scenario UUID -> scenario object
            const scenarioByUuid = new Map(discovery.scenarios.map(s => [s.uuid, s]));

            return (
              <React.Fragment key={courseId}>
                {/* Course Header */}
                <ListItem
                  sx={{
                    bgcolor: 'action.hover',
                    borderBottom: 1,
                    borderColor: 'divider',
                    py: 1
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SchoolIcon fontSize="small" />
                        <Typography variant="body2" fontWeight="bold">
                          {course?.fullname || `Course ${courseId}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(discovery.cmi5Activities || []).length} CMI5 activit{(discovery.cmi5Activities || []).length !== 1 ? 'ies' : 'y'}
                        </Typography>
                        {discovery.scenarios.length > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            | {discovery.scenarios.length} scenario{discovery.scenarios.length !== 1 ? 's' : ''}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <IconButton
                    size="small"
                    onClick={() => discoverScenariosForCourses([courseId], true)}
                    disabled={loadingScenarios.get(courseId)}
                    title="Refresh for this course"
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </ListItem>

                {/* CMI5 Activities → AUs → inline draft toggles */}
                {(discovery.cmi5Activities || []).map(activity => (
                  <React.Fragment key={activity.activityId}>
                    <ListItem sx={{ pl: 4, py: 0.5 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ScienceIcon fontSize="small" color="primary" />
                            <Typography variant="body2" fontWeight="medium">
                              {activity.activityName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ({activity.aus.length} AU{activity.aus.length !== 1 ? 's' : ''})
                            </Typography>
                          </Box>
                        }
                        secondary={activity.tenantId ? (
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                            tenant: {activity.tenantId} / course: {activity.cptPlayerCourseId}
                          </Typography>
                        ) : undefined}
                      />
                    </ListItem>
                    {/* AUs for this activity */}
                    {activity.aus.map(au => {
                      const linkedScenario = au.rangeosScenarioUUID ? scenarioByUuid.get(au.rangeosScenarioUUID) : undefined;
                      return (
                        <React.Fragment key={au.auId}>
                          <ListItem sx={{ pl: 8, py: 0.15 }}>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'info.main', fontWeight: 'bold' }}>
                                    AU
                                  </Typography>
                                  <Typography variant="caption">
                                    {au.auTitle}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }}>
                                  {au.auId}
                                </Typography>
                              }
                            />
                          </ListItem>
                          {/* Draft toggle inline with the AU */}
                          {linkedScenario && (
                            <ListItem
                              button
                              onClick={() => toggleScenario(courseId, linkedScenario.uuid)}
                              sx={{ pl: 12, py: 0.15 }}
                            >
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <Checkbox
                                  size="small"
                                  checked={courseSelectedScenarios.includes(linkedScenario.uuid)}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={() => toggleScenario(courseId, linkedScenario.uuid)}
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="caption" fontWeight="bold">
                                      {linkedScenario.name}
                                    </Typography>
                                    {linkedScenario.hasDraft ? (
                                      <Typography
                                        variant="caption"
                                        sx={{ bgcolor: 'success.light', color: 'success.contrastText', px: 0.5, py: 0.1, borderRadius: 0.5, fontSize: '0.6rem' }}
                                      >
                                        Has Draft
                                      </Typography>
                                    ) : (
                                      <Typography
                                        variant="caption"
                                        sx={{ bgcolor: 'action.selected', color: 'text.secondary', px: 0.5, py: 0.1, borderRadius: 0.5, fontSize: '0.6rem' }}
                                      >
                                        No Draft
                                      </Typography>
                                    )}
                                  </Box>
                                }
                                secondary={
                                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.6rem' }}>
                                    {linkedScenario.uuid}{linkedScenario.draftId ? ` | draft: ${linkedScenario.draftId}` : ''}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                ))}
              </React.Fragment>
            );
          })}
          </List>
        </Paper>

        {totalScenariosSelected === 0 && !isScanningScenarios && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No scenarios selected. Scenarios are optional - click Next to continue without them.
          </Alert>
        )}
        </>
      )}
    </Box>
  );
};

// Step 5: Select Frameworks (dynamic step - shown if frameworks content type is selected)
const FrameworkSelectionStep: React.FC = () => {
  const wizard = useWizard();
  const [frameworks, setFrameworks] = useState<any[]>([]);
  const [loadingFrameworks, setLoadingFrameworks] = useState(false);
  const [selectedFrameworkIds, setSelectedFrameworkIds] = useState<number[]>(
    wizard.getData('selectedFrameworkIds', [])
  );

  const toggleFramework = (frameworkId: number) => {
    setSelectedFrameworkIds(prev =>
      prev.includes(frameworkId)
        ? prev.filter(id => id !== frameworkId)
        : [...prev, frameworkId]
    );
  };

  const toggleAllFrameworks = () => {
    if (selectedFrameworkIds.length === frameworks.length) {
      setSelectedFrameworkIds([]);
    } else {
      setSelectedFrameworkIds(frameworks.map(f => f.id));
    }
  };

  // Load frameworks when component mounts
  useEffect(() => {
    const sourceId = wizard.getData('sourceId');
    if (!sourceId) return;

    const loadFrameworks = async () => {
      setLoadingFrameworks(true);
      try {
        const frameworksData = await moodleApi.listCompetencyFrameworks(sourceId);
        setFrameworks(frameworksData);
      } catch (err: any) {
        wizard.setError(`Failed to load frameworks: ${err.message}`);
      } finally {
        setLoadingFrameworks(false);
      }
    };
    loadFrameworks();
  }, []);

  // Update wizard data when selection changes
  useEffect(() => {
    wizard.setData('selectedFrameworkIds', selectedFrameworkIds);
    wizard.setData('selectedFrameworks', frameworks.filter(f => selectedFrameworkIds.includes(f.id)));
  }, [selectedFrameworkIds, frameworks]);

  // Update validation when wizard data changes
  useEffect(() => {
    const frameworkIds = wizard.getData<number[]>('selectedFrameworkIds', []);
    // Calculate this step's index based on what's selected
    const contentTypes = wizard.getData<string[]>('contentTypes', []);
    let stepIndex = 2; // Start after Source=0, ContentTypes=1
    if (contentTypes.includes('categories')) stepIndex++; // Categories
    if (contentTypes.includes('courses')) {
      stepIndex++; // Courses
      stepIndex++; // Scenarios (always shown when courses selected)
    }
    // Now at frameworks position
    // Step is valid if at least one framework is selected
    wizard.setStepValidation(stepIndex, frameworkIds.length > 0);
  }, [wizard.data]);

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom fontWeight="bold">
        Select Competency Frameworks to Backup
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose which competency frameworks you want to backup.
      </Typography>

      {loadingFrameworks ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={40} />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedFrameworkIds.length === frameworks.length && frameworks.length > 0}
                  indeterminate={selectedFrameworkIds.length > 0 && selectedFrameworkIds.length < frameworks.length}
                  onChange={toggleAllFrameworks}
                />
              }
              label="Select All Frameworks"
            />
            <Typography variant="body2" color="text.secondary">
              {selectedFrameworkIds.length} of {frameworks.length} selected
            </Typography>
          </Box>

          <Paper variant="outlined" sx={{ maxHeight: 500, overflow: 'auto' }}>
            <List dense>
              {frameworks.map(framework => (
                <ListItem
                  key={framework.id}
                  button
                  onClick={() => toggleFramework(framework.id)}
                >
                  <ListItemIcon>
                    <Checkbox checked={selectedFrameworkIds.includes(framework.id)} />
                  </ListItemIcon>
                  <ListItemText
                    primary={framework.shortname}
                    secondary={framework.idnumber ? `ID: ${framework.idnumber}` : undefined}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>

          {selectedFrameworkIds.length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Please select at least one competency framework to continue.
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};

// Step 6: Select Learning Path Templates (dynamic step - shown if learning-paths content type is selected)
const LearningPathSelectionStep: React.FC = () => {
  const wizard = useWizard();
  const [templates, setTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<number[]>(
    wizard.getData('selectedTemplateIds', [])
  );

  const toggleTemplate = (templateId: number) => {
    setSelectedTemplateIds(prev =>
      prev.includes(templateId)
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId]
    );
  };

  const toggleAllTemplates = () => {
    if (selectedTemplateIds.length === templates.length) {
      setSelectedTemplateIds([]);
    } else {
      setSelectedTemplateIds(templates.map(t => t.id));
    }
  };

  // Load templates when component mounts
  useEffect(() => {
    const sourceId = wizard.getData('sourceId');
    if (!sourceId) return;

    const loadTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const templatesData = await moodleApi.listLearningPathTemplates(sourceId);
        setTemplates(templatesData);
      } catch (err: any) {
        wizard.setError(`Failed to load templates: ${err.message}`);
      } finally {
        setLoadingTemplates(false);
      }
    };
    loadTemplates();
  }, []);

  // Update wizard data when selection changes
  useEffect(() => {
    wizard.setData('selectedTemplateIds', selectedTemplateIds);
    wizard.setData('selectedTemplates', templates.filter(t => selectedTemplateIds.includes(t.id)));
  }, [selectedTemplateIds, templates]);

  // Update validation when wizard data changes
  useEffect(() => {
    const templateIds = wizard.getData<number[]>('selectedTemplateIds', []);
    // Calculate this step's index based on what's selected
    const contentTypes = wizard.getData<string[]>('contentTypes', []);
    let stepIndex = 2; // Start after Source=0, ContentTypes=1
    if (contentTypes.includes('categories')) stepIndex++; // Categories
    if (contentTypes.includes('courses')) {
      stepIndex++; // Courses
      stepIndex++; // Scenarios (always shown when courses selected)
    }
    if (contentTypes.includes('frameworks')) stepIndex++; // Frameworks
    // Now at learning paths position
    // Step is valid if at least one template is selected
    wizard.setStepValidation(stepIndex, templateIds.length > 0);
  }, [wizard.data]);

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom fontWeight="bold">
        Select Learning Path Templates to Backup
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose which learning path templates you want to backup.
      </Typography>

      {loadingTemplates ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={40} />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedTemplateIds.length === templates.length && templates.length > 0}
                  indeterminate={selectedTemplateIds.length > 0 && selectedTemplateIds.length < templates.length}
                  onChange={toggleAllTemplates}
                />
              }
              label="Select All Templates"
            />
            <Typography variant="body2" color="text.secondary">
              {selectedTemplateIds.length} of {templates.length} selected
            </Typography>
          </Box>

          <Paper variant="outlined" sx={{ maxHeight: 500, overflow: 'auto' }}>
            <List dense>
              {templates.map(template => (
                <ListItem
                  key={template.id}
                  button
                  onClick={() => toggleTemplate(template.id)}
                >
                  <ListItemIcon>
                    <Checkbox checked={selectedTemplateIds.includes(template.id)} />
                  </ListItemIcon>
                  <ListItemText
                    primary={template.name}
                    secondary={template.idnumber ? `ID: ${template.idnumber}` : (template.description || undefined)}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>

          {selectedTemplateIds.length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Please select at least one learning path template to continue.
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};

// Step 2: Select Content Types (DEPRECATED - keeping for reference, will be removed)
const ContentSelectionStep: React.FC = () => {
  const wizard = useWizard();
  const [courses, setCourses] = useState<MoodleCourse[]>([]);
  const [frameworks, setFrameworks] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingFrameworks, setLoadingFrameworks] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedCourseIds, setSelectedCourseIds] = useState<number[]>(
    wizard.getData('selectedCourseIds', [])
  );
  const [selectedFrameworkIds, setSelectedFrameworkIds] = useState<number[]>(
    wizard.getData('selectedFrameworkIds', [])
  );
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<number[]>(
    wizard.getData('selectedTemplateIds', [])
  );
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Scenario discovery state
  const [courseScenarios, setCourseScenarios] = useState<Map<number, CourseScenarioDiscoveryResult>>(new Map());
  const [loadingScenarios, setLoadingScenarios] = useState<Map<number, boolean>>(new Map());
  const [selectedScenarioUuids, setSelectedScenarioUuids] = useState<Map<number, string[]>>(
    wizard.getData('selectedScenarioUuids', new Map())
  );
  const [scenariosExpanded, setScenariosExpanded] = useState(false);

  const contentTypes = wizard.getData<string[]>('contentTypes', []);

  // Build hierarchical category tree from path strings
  interface CategoryNode {
    name: string;
    fullPath: string;
    courses: MoodleCourse[];
    children: CategoryNode[];
  }

  const categoryTree = useMemo(() => {
    const root: CategoryNode = { name: '', fullPath: '', courses: [], children: [] };

    courses.forEach(course => {
      const categoryPath = (course.category as any) || 'Uncategorized';
      const parts = categoryPath.split(' / ');

      let currentNode = root;
      let currentPath = '';

      parts.forEach((part, index) => {
        currentPath = index === 0 ? part : `${currentPath} / ${part}`;

        let childNode = currentNode.children.find(c => c.name === part);
        if (!childNode) {
          childNode = { name: part, fullPath: currentPath, courses: [], children: [] };
          currentNode.children.push(childNode);
        }

        // If this is the last part, add the course here
        if (index === parts.length - 1) {
          childNode.courses.push(course);
        }

        currentNode = childNode;
      });
    });

    return root.children;
  }, [courses]);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  // Get all courses in a category node and its children recursively
  const getAllCoursesInNode = (node: CategoryNode): MoodleCourse[] => {
    const courses = [...node.courses];
    node.children.forEach(child => {
      courses.push(...getAllCoursesInNode(child));
    });
    return courses;
  };

  // Render category tree recursively
  const renderCategoryNode = (node: CategoryNode, level: number = 0): React.ReactNode => {
    const isExpanded = expandedCategories.has(node.fullPath);
    const allCoursesInNode = getAllCoursesInNode(node);
    const categorySelected = allCoursesInNode.length > 0 && allCoursesInNode.every(c => selectedCourseIds.includes(c.id));
    const categorySomeSelected = allCoursesInNode.some(c => selectedCourseIds.includes(c.id)) && !categorySelected;

    return (
      <React.Fragment key={node.fullPath}>
        {/* Category Header */}
        <ListItem
          sx={{
            bgcolor: level === 0 ? 'action.hover' : 'transparent',
            fontWeight: level === 0 ? 'bold' : 'normal',
            borderBottom: level === 0 ? 1 : 0,
            borderColor: 'divider',
            pl: 2 + level * 2
          }}
        >
          <ListItemIcon>
            <IconButton
              size="small"
              onClick={() => toggleCategory(node.fullPath)}
            >
              {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </IconButton>
          </ListItemIcon>
          <ListItemIcon>
            <Checkbox
              checked={categorySelected}
              indeterminate={categorySomeSelected}
              onChange={() => {
                if (categorySelected) {
                  // Deselect all in category
                  setSelectedCourseIds(prev =>
                    prev.filter(id => !allCoursesInNode.some(c => c.id === id))
                  );
                } else {
                  // Select all in category
                  setSelectedCourseIds(prev => {
                    const newIds = allCoursesInNode.map(c => c.id).filter(id => !prev.includes(id));
                    return [...prev, ...newIds];
                  });
                }
              }}
            />
          </ListItemIcon>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CategoryIcon fontSize="small" />
                <Typography variant="body2" fontWeight={level === 0 ? 'bold' : 'normal'}>
                  {node.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ({allCoursesInNode.length} course{allCoursesInNode.length !== 1 ? 's' : ''})
                </Typography>
              </Box>
            }
          />
        </ListItem>

        {/* Courses and subcategories */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {/* Render child categories */}
          {node.children.map(child => renderCategoryNode(child, level + 1))}

          {/* Render courses directly in this category */}
          {node.courses.map(course => (
            <ListItem
              key={course.id}
              button
              onClick={() => toggleCourse(course.id)}
              sx={{ pl: 8 + level * 2 }}
            >
              <ListItemIcon>
                <Checkbox checked={selectedCourseIds.includes(course.id)} />
              </ListItemIcon>
              <ListItemText
                primary={course.fullname}
                secondary={`Short name: ${course.shortname}`}
              />
            </ListItem>
          ))}
        </Collapse>
      </React.Fragment>
    );
  };

  useEffect(() => {
    const sourceId = wizard.getData('sourceId');
    if (!sourceId) return;

    // Load courses if "courses" is selected
    if (contentTypes.includes('courses')) {
      const loadCourses = async () => {
        setLoadingCourses(true);
        try {
          const coursesData = await moodleApi.listCourses(sourceId);
          setCourses(coursesData);
        } catch (err: any) {
          wizard.setError(`Failed to load courses: ${err.message}`);
        } finally {
          setLoadingCourses(false);
        }
      };
      loadCourses();
    }

    // Load frameworks if "frameworks" is selected
    if (contentTypes.includes('frameworks')) {
      const loadFrameworks = async () => {
        setLoadingFrameworks(true);
        try {
          console.log('Fetching competency frameworks for sourceId:', sourceId);
          const frameworksData = await moodleApi.listCompetencyFrameworks(sourceId);
          console.log('Received frameworks:', frameworksData);
          setFrameworks(frameworksData);
        } catch (err: any) {
          console.error('Error loading frameworks:', err);
          wizard.setError(`Failed to load frameworks: ${err.message}`);
        } finally {
          setLoadingFrameworks(false);
        }
      };
      loadFrameworks();
    }

    // Load templates if "learning-paths" is selected
    if (contentTypes.includes('learning-paths')) {
      const loadTemplates = async () => {
        setLoadingTemplates(true);
        try {
          const templatesData = await moodleApi.listLearningPathTemplates(sourceId);
          setTemplates(templatesData);
        } catch (err: any) {
          wizard.setError(`Failed to load templates: ${err.message}`);
        } finally {
          setLoadingTemplates(false);
        }
      };
      loadTemplates();
    }
  }, [contentTypes]);

  // Discover scenarios for selected courses
  const discoverScenariosForCourses = async (courseIds: number[], forceRefresh: boolean = false) => {
    const sourceId = wizard.getData('sourceId');
    if (!sourceId) return;

    for (const courseId of courseIds) {
      // Skip if already loading
      if (loadingScenarios.get(courseId)) {
        continue;
      }

      // Skip if already loaded and not forcing refresh
      if (!forceRefresh && courseScenarios.has(courseId)) {
        continue;
      }

      // Mark as loading
      setLoadingScenarios(prev => new Map(prev).set(courseId, true));

      try {
        const discovery = await moodleApi.discoverScenariosForCourse(sourceId, courseId, forceRefresh);
        setCourseScenarios(prev => new Map(prev).set(courseId, discovery));
      } catch (err: any) {
        console.error(`Failed to discover scenarios for course ${courseId}:`, err);
        // Continue with other courses
      } finally {
        setLoadingScenarios(prev => {
          const newMap = new Map(prev);
          newMap.delete(courseId);
          return newMap;
        });
      }
    }
  };

  useEffect(() => {
    if (!contentTypes.includes('courses') || selectedCourseIds.length === 0) {
      return;
    }

    discoverScenariosForCourses(selectedCourseIds, false);
  }, [selectedCourseIds, contentTypes]);

  const toggleContentType = (type: string) => {
    const current = wizard.getData<string[]>('contentTypes', []);
    if (current.includes(type)) {
      wizard.setData('contentTypes', current.filter(t => t !== type));
      if (type === 'courses') {
        setSelectedCourseIds([]);
      } else if (type === 'frameworks') {
        setSelectedFrameworkIds([]);
      } else if (type === 'learning-paths') {
        setSelectedTemplateIds([]);
      }
    } else {
      wizard.setData('contentTypes', [...current, type]);
    }
  };

  const toggleCourse = (courseId: number) => {
    setSelectedCourseIds(prev =>
      prev.includes(courseId)
        ? prev.filter(id => id !== courseId)
        : [...prev, courseId]
    );
  };

  const toggleAllCourses = () => {
    if (selectedCourseIds.length === courses.length) {
      setSelectedCourseIds([]);
    } else {
      setSelectedCourseIds(courses.map(c => c.id));
    }
  };

  const toggleFramework = (frameworkId: number) => {
    setSelectedFrameworkIds(prev =>
      prev.includes(frameworkId)
        ? prev.filter(id => id !== frameworkId)
        : [...prev, frameworkId]
    );
  };

  const toggleAllFrameworks = () => {
    if (selectedFrameworkIds.length === frameworks.length) {
      setSelectedFrameworkIds([]);
    } else {
      setSelectedFrameworkIds(frameworks.map(f => f.id));
    }
  };

  const toggleTemplate = (templateId: number) => {
    setSelectedTemplateIds(prev =>
      prev.includes(templateId)
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId]
    );
  };

  const toggleAllTemplates = () => {
    if (selectedTemplateIds.length === templates.length) {
      setSelectedTemplateIds([]);
    } else {
      setSelectedTemplateIds(templates.map(t => t.id));
    }
  };

  const toggleScenario = (courseId: number, scenarioUuid: string) => {
    setSelectedScenarioUuids(prev => {
      const newMap = new Map(prev);
      const courseScenarios = newMap.get(courseId) || [];

      if (courseScenarios.includes(scenarioUuid)) {
        // Remove scenario
        const filtered = courseScenarios.filter(uuid => uuid !== scenarioUuid);
        if (filtered.length === 0) {
          newMap.delete(courseId);
        } else {
          newMap.set(courseId, filtered);
        }
      } else {
        // Add scenario
        newMap.set(courseId, [...courseScenarios, scenarioUuid]);
      }

      return newMap;
    });
  };

  const toggleAllScenariosForCourse = (courseId: number, scenarios: DiscoveredScenario[]) => {
    setSelectedScenarioUuids(prev => {
      const newMap = new Map(prev);
      const courseScenarios = newMap.get(courseId) || [];
      const allSelected = scenarios.every(s => courseScenarios.includes(s.uuid));

      if (allSelected) {
        // Deselect all
        newMap.delete(courseId);
      } else {
        // Select all
        newMap.set(courseId, scenarios.map(s => s.uuid));
      }

      return newMap;
    });
  };

  // Count total scenarios discovered across all selected courses
  const totalScenariosDiscovered = useMemo(() => {
    let count = 0;
    courseScenarios.forEach(discovery => {
      count += discovery.scenarios.length;
    });
    return count;
  }, [courseScenarios]);

  // Count total scenarios selected
  const totalScenariosSelected = useMemo(() => {
    let count = 0;
    selectedScenarioUuids.forEach(uuids => {
      count += uuids.length;
    });
    return count;
  }, [selectedScenarioUuids]);

  // Check if any courses are currently being scanned for scenarios
  const isScanningScenarios = useMemo(() => {
    return Array.from(loadingScenarios.values()).some(loading => loading);
  }, [loadingScenarios]);

  // Auto-expand scenarios section when scenarios are discovered
  useEffect(() => {
    if (totalScenariosDiscovered > 0 && !isScanningScenarios) {
      setScenariosExpanded(true);
    }
  }, [totalScenariosDiscovered, isScanningScenarios]);

  // Validation effect - must be after all useMemo declarations
  useEffect(() => {
    wizard.setData('selectedCourseIds', selectedCourseIds);
    wizard.setData('selectedCourses', courses.filter(c => selectedCourseIds.includes(c.id)));
    wizard.setData('selectedFrameworkIds', selectedFrameworkIds);
    wizard.setData('selectedFrameworks', frameworks.filter(f => selectedFrameworkIds.includes(f.id)));
    wizard.setData('selectedTemplateIds', selectedTemplateIds);
    wizard.setData('selectedTemplates', templates.filter(t => selectedTemplateIds.includes(t.id)));
    wizard.setData('selectedScenarioUuids', selectedScenarioUuids);

    // Step is valid if at least one content type is selected
    // AND for each selected type, at least one item must be selected
    const hasContentType = contentTypes.length > 0;
    const coursesValid = !contentTypes.includes('courses') || selectedCourseIds.length > 0;
    const frameworksValid = !contentTypes.includes('frameworks') || selectedFrameworkIds.length > 0;
    const templatesValid = !contentTypes.includes('learning-paths') || selectedTemplateIds.length > 0;

    // IMPORTANT: If courses are selected, wait for scenario discovery to complete
    // This prevents users from clicking Next before scenarios are loaded
    const scenarioDiscoveryComplete = !contentTypes.includes('courses') || !isScanningScenarios;

    wizard.setStepValidation(1, hasContentType && coursesValid && frameworksValid && templatesValid && scenarioDiscoveryComplete);
  }, [contentTypes, selectedCourseIds, courses, selectedFrameworkIds, frameworks, selectedTemplateIds, templates, selectedScenarioUuids, isScanningScenarios]);

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom fontWeight="bold">
        Select Content Types to Backup
      </Typography>

      {/* Scanning scenarios alert - shown at top for visibility */}
      {isScanningScenarios && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<CircularProgress size={20} />}>
          <Typography variant="body2" fontWeight="bold">
            Scanning for RangeOS scenario linkages...
          </Typography>
          <Typography variant="caption">
            The "Next" button will be enabled once scanning is complete. This ensures you don't miss any scenarios linked to your selected courses.
          </Typography>
        </Alert>
      )}

      {/* Success message when scenarios are discovered */}
      {!isScanningScenarios && totalScenariosDiscovered > 0 && contentTypes.includes('courses') && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="bold">
            {totalScenariosDiscovered} RangeOS scenario(s) discovered!
          </Typography>
          <Typography variant="caption">
            {totalScenariosSelected > 0
              ? `You have selected ${totalScenariosSelected} scenario(s) to include in your export.`
              : 'Scroll down to the "RangeOS Scenarios" section to optionally include them in your export.'}
          </Typography>
        </Alert>
      )}

      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={contentTypes.includes('courses')}
              onChange={() => toggleContentType('courses')}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SchoolIcon fontSize="small" />
              <Typography>Courses</Typography>
            </Box>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={contentTypes.includes('categories')}
              onChange={() => toggleContentType('categories')}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CategoryIcon fontSize="small" />
              <Typography>Categories</Typography>
            </Box>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={contentTypes.includes('frameworks')}
              onChange={() => toggleContentType('frameworks')}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ExtensionIcon fontSize="small" />
              <Typography>Competency Frameworks</Typography>
            </Box>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={contentTypes.includes('learning-paths')}
              onChange={() => toggleContentType('learning-paths')}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ExtensionIcon fontSize="small" />
              <Typography>Learning Path Templates</Typography>
            </Box>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={contentTypes.includes('cmi5')}
              onChange={() => toggleContentType('cmi5')}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ExtensionIcon fontSize="small" color="primary" />
              <Typography>CMI5 Activities</Typography>
            </Box>
          }
        />
      </FormGroup>

      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2" fontWeight="bold">
          About CMI5 Content:
        </Typography>
        <Typography variant="body2">
          When exporting courses, any associated CMI5 activities are automatically exported with the courses.
          You don't need to select CMI5 separately unless you want to export CMI5 activities that aren't linked to courses.
        </Typography>
      </Alert>

      {contentTypes.includes('courses') && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Select Courses
          </Typography>

          {loadingCourses ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={30} />
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedCourseIds.length === courses.length && courses.length > 0}
                      indeterminate={selectedCourseIds.length > 0 && selectedCourseIds.length < courses.length}
                      onChange={toggleAllCourses}
                    />
                  }
                  label="Select All Courses"
                />
                <Typography variant="body2" color="text.secondary">
                  {selectedCourseIds.length} of {courses.length} selected
                </Typography>
              </Box>

              <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
                <List dense>
                  {categoryTree.map(node => renderCategoryNode(node, 0))}
                </List>
              </Paper>
            </>
          )}
        </Box>
      )}

      {/* RangeOS Scenarios Section */}
      {contentTypes.includes('courses') && totalScenariosDiscovered > 0 && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <IconButton
              size="small"
              onClick={() => setScenariosExpanded(!scenariosExpanded)}
            >
              {scenariosExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </IconButton>
            <ScienceIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight="bold">
              RangeOS Scenarios ({totalScenariosDiscovered} found)
            </Typography>
            {totalScenariosSelected > 0 && (
              <Typography variant="body2" color="primary">
                {totalScenariosSelected} selected
              </Typography>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => discoverScenariosForCourses(selectedCourseIds, true)}
              disabled={isScanningScenarios}
              title="Refresh all scenarios"
            >
              Refresh All
            </Button>
          </Box>

          {isScanningScenarios && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Box>
                  <Typography variant="body2">
                    <strong>Scanning courses for draft linkages...</strong> Checking CMI5 activities for RangeOS scenario connections.
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    Please wait for scanning to complete before proceeding to the next step.
                  </Typography>
                </Box>
              </Box>
            </Alert>
          )}

          {!isScanningScenarios && totalScenariosDiscovered > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>RangeOS Scenarios:</strong> The system discovered {totalScenariosDiscovered} scenario(s) linked to your selected courses via CMI5 activities.
                You can optionally include these scenarios in your export.
              </Typography>
            </Alert>
          )}

          <Collapse in={scenariosExpanded} timeout="auto" unmountOnExit>
            <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
              <List dense>
                {selectedCourseIds.map(courseId => {
                  const discovery = courseScenarios.get(courseId);
                  const course = courses.find(c => c.id === courseId);
                  const isLoading = loadingScenarios.get(courseId);

                  // Show loading indicator for courses being scanned
                  if (isLoading) {
                    return (
                      <ListItem
                        key={courseId}
                        sx={{
                          bgcolor: 'action.hover',
                          borderBottom: 1,
                          borderColor: 'divider',
                          py: 1
                        }}
                      >
                        <ListItemIcon>
                          <CircularProgress size={20} />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <SchoolIcon fontSize="small" />
                              <Typography variant="body2">
                                {course?.fullname || `Course ${courseId}`}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" color="primary">
                              Looking for draft linkages...
                            </Typography>
                          }
                        />
                      </ListItem>
                    );
                  }

                  if (!discovery || discovery.scenarios.length === 0) {
                    return null;
                  }

                  const courseSelectedScenarios = selectedScenarioUuids.get(courseId) || [];
                  const allSelected = discovery.scenarios.every(s => courseSelectedScenarios.includes(s.uuid));
                  const someSelected = courseSelectedScenarios.length > 0 && !allSelected;

                  return (
                    <React.Fragment key={courseId}>
                      {/* Course Header */}
                      <ListItem
                        sx={{
                          bgcolor: 'action.hover',
                          borderBottom: 1,
                          borderColor: 'divider',
                          py: 1
                        }}
                      >
                        <ListItemIcon>
                          <Checkbox
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={() => toggleAllScenariosForCourse(courseId, discovery.scenarios)}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <SchoolIcon fontSize="small" />
                              <Typography variant="body2" fontWeight="bold">
                                {course?.fullname || `Course ${courseId}`}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ({discovery.scenarios.length} scenario{discovery.scenarios.length !== 1 ? 's' : ''})
                              </Typography>
                            </Box>
                          }
                        />
                        <IconButton
                          size="small"
                          onClick={() => discoverScenariosForCourses([courseId], true)}
                          disabled={loadingScenarios.get(courseId)}
                          title="Refresh scenarios for this course"
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </ListItem>

                      {/* Scenarios */}
                      {discovery.scenarios.map(scenario => (
                        <ListItem
                          key={scenario.uuid}
                          button
                          onClick={() => toggleScenario(courseId, scenario.uuid)}
                          sx={{ pl: 8 }}
                        >
                          <ListItemIcon>
                            <Checkbox checked={courseSelectedScenarios.includes(scenario.uuid)} />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ScienceIcon fontSize="small" />
                                <Typography variant="body2">
                                  {scenario.name}
                                </Typography>
                                {scenario.hasDraft && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      bgcolor: 'success.light',
                                      color: 'success.contrastText',
                                      px: 0.5,
                                      py: 0.25,
                                      borderRadius: 0.5
                                    }}
                                  >
                                    Has Draft
                                  </Typography>
                                )}
                                {!scenario.hasDraft && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      bgcolor: 'action.selected',
                                      color: 'text.secondary',
                                      px: 0.5,
                                      py: 0.25,
                                      borderRadius: 0.5
                                    }}
                                  >
                                    No Draft
                                  </Typography>
                                )}
                              </Box>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                Linked to {scenario.cmi5Activities.length} CMI5 activit{scenario.cmi5Activities.length !== 1 ? 'ies' : 'y'}:
                                {' '}
                                {scenario.cmi5Activities.map(a => a.activityName).join(', ')}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </React.Fragment>
                  );
                })}
              </List>
            </Paper>
          </Collapse>
        </Box>
      )}

      {contentTypes.includes('frameworks') && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Select Competency Frameworks
          </Typography>

          {loadingFrameworks ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={30} />
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedFrameworkIds.length === frameworks.length && frameworks.length > 0}
                      indeterminate={selectedFrameworkIds.length > 0 && selectedFrameworkIds.length < frameworks.length}
                      onChange={toggleAllFrameworks}
                    />
                  }
                  label="Select All Frameworks"
                />
                <Typography variant="body2" color="text.secondary">
                  {selectedFrameworkIds.length} of {frameworks.length} selected
                </Typography>
              </Box>

              <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                <List dense>
                  {frameworks.map(framework => (
                    <ListItem
                      key={framework.id}
                      button
                      onClick={() => toggleFramework(framework.id)}
                    >
                      <ListItemIcon>
                        <Checkbox checked={selectedFrameworkIds.includes(framework.id)} />
                      </ListItemIcon>
                      <ListItemText
                        primary={framework.shortname}
                        secondary={framework.idnumber ? `ID: ${framework.idnumber}` : undefined}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </>
          )}
        </Box>
      )}

      {contentTypes.includes('learning-paths') && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Select Learning Path Templates
          </Typography>

          {loadingTemplates ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={30} />
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedTemplateIds.length === templates.length && templates.length > 0}
                      indeterminate={selectedTemplateIds.length > 0 && selectedTemplateIds.length < templates.length}
                      onChange={toggleAllTemplates}
                    />
                  }
                  label="Select All Templates"
                />
                <Typography variant="body2" color="text.secondary">
                  {selectedTemplateIds.length} of {templates.length} selected
                </Typography>
              </Box>

              <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                <List dense>
                  {templates.map(template => (
                    <ListItem
                      key={template.id}
                      button
                      onClick={() => toggleTemplate(template.id)}
                    >
                      <ListItemIcon>
                        <Checkbox checked={selectedTemplateIds.includes(template.id)} />
                      </ListItemIcon>
                      <ListItemText
                        primary={template.name}
                        secondary={template.idnumber ? `ID: ${template.idnumber}` : (template.description || undefined)}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </>
          )}
        </Box>
      )}

      {contentTypes.length === 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Please select at least one content type to backup.
        </Alert>
      )}
    </Box>
  );
};

// Step: Select CMI5 Activities (dynamic step - shown if cmi5 content type is selected)
interface CMI5Activity {
  tenantId: string;
  courseId: string;
  activityId: number;
  activityName: string;
  cmi5activityid: string;
  moodleCourseId: number;
}

const CMI5ActivitySelectionStep: React.FC = () => {
  const wizard = useWizard();
  const [activities, setActivities] = useState<CMI5Activity[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedActivities, setSelectedActivities] = useState<CMI5Activity[]>(
    wizard.getData('selectedCMI5Activities', [])
  );
  const [expandedCourses, setExpandedCourses] = useState<Set<number>>(new Set());
  const [courseNames, setCourseNames] = useState<Map<number, string>>(new Map());

  // Group activities by Moodle course ID
  const groupedActivities = useMemo(() => {
    const groups = new Map<number, CMI5Activity[]>();
    activities.forEach(activity => {
      const courseId = activity.moodleCourseId;
      if (!groups.has(courseId)) {
        groups.set(courseId, []);
      }
      groups.get(courseId)!.push(activity);
    });
    return groups;
  }, [activities]);

  const selectedIds = useMemo(() => new Set(selectedActivities.map(a => a.activityId)), [selectedActivities]);

  const toggleCourse = (courseId: number) => {
    setExpandedCourses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(courseId)) {
        newSet.delete(courseId);
      } else {
        newSet.add(courseId);
      }
      return newSet;
    });
  };

  const toggleActivity = (activity: CMI5Activity) => {
    setSelectedActivities(prev => {
      if (prev.some(a => a.activityId === activity.activityId)) {
        return prev.filter(a => a.activityId !== activity.activityId);
      }
      return [...prev, activity];
    });
  };

  const toggleCourseActivities = (courseId: number) => {
    const courseActivities = groupedActivities.get(courseId) || [];
    const allSelected = courseActivities.every(a => selectedIds.has(a.activityId));
    if (allSelected) {
      setSelectedActivities(prev => prev.filter(a => a.moodleCourseId !== courseId));
    } else {
      setSelectedActivities(prev => {
        const existing = prev.filter(a => a.moodleCourseId !== courseId);
        return [...existing, ...courseActivities];
      });
    }
  };

  const toggleAll = () => {
    if (selectedActivities.length === activities.length) {
      setSelectedActivities([]);
    } else {
      setSelectedActivities([...activities]);
    }
  };

  // Load activities on mount
  useEffect(() => {
    const sourceId = wizard.getData('sourceId');
    if (!sourceId) return;

    const load = async () => {
      setLoading(true);
      try {
        const [data, courses] = await Promise.all([
          moodleApi.listAllCMI5Activities(sourceId),
          moodleApi.listCourses(sourceId),
        ]);
        setActivities(data);
        // Build course name lookup
        const nameMap = new Map<number, string>();
        courses.forEach(c => nameMap.set(c.id, c.fullname));
        setCourseNames(nameMap);
        // Auto-expand all courses
        setExpandedCourses(new Set(data.map((a: CMI5Activity) => a.moodleCourseId)));
      } catch (err: any) {
        wizard.setError(`Failed to load CMI5 activities: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Update wizard data when selection changes
  useEffect(() => {
    wizard.setData('selectedCMI5Activities', selectedActivities);
  }, [selectedActivities]);

  // Update step validation
  useEffect(() => {
    const contentTypes = wizard.getData<string[]>('contentTypes', []);
    let stepIndex = 2;
    if (contentTypes.includes('categories')) stepIndex++;
    if (contentTypes.includes('courses')) {
      stepIndex++;
      stepIndex++;
    }
    if (contentTypes.includes('frameworks')) stepIndex++;
    if (contentTypes.includes('learning-paths')) stepIndex++;
    // Now at CMI5 position
    wizard.setStepValidation(stepIndex, selectedActivities.length > 0);
  }, [wizard.data]);

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom fontWeight="bold">
        Select CMI5 Activities to Export
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose which CMI5 activities you want to export individually. Activities are grouped by their Moodle course.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={40} />
        </Box>
      ) : activities.length === 0 ? (
        <Alert severity="info">No CMI5 activities found in this Moodle instance.</Alert>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedActivities.length === activities.length && activities.length > 0}
                  indeterminate={selectedActivities.length > 0 && selectedActivities.length < activities.length}
                  onChange={toggleAll}
                />
              }
              label="Select All Activities"
            />
            <Typography variant="body2" color="text.secondary">
              {selectedActivities.length} of {activities.length} selected
            </Typography>
          </Box>

          <Paper variant="outlined" sx={{ maxHeight: 500, overflow: 'auto' }}>
            <List dense>
              {Array.from(groupedActivities.entries()).map(([courseId, courseActivities]) => {
                const isExpanded = expandedCourses.has(courseId);
                const allSelected = courseActivities.every(a => selectedIds.has(a.activityId));
                const someSelected = courseActivities.some(a => selectedIds.has(a.activityId)) && !allSelected;

                return (
                  <React.Fragment key={courseId}>
                    <ListItem
                      sx={{
                        bgcolor: 'action.hover',
                        fontWeight: 'bold',
                        borderBottom: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <ListItemIcon>
                        <IconButton size="small" onClick={() => toggleCourse(courseId)}>
                          {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                        </IconButton>
                      </ListItemIcon>
                      <ListItemIcon>
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected}
                          onChange={() => toggleCourseActivities(courseId)}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SchoolIcon fontSize="small" />
                            <Typography variant="body2" fontWeight="bold">
                              {courseNames.get(courseId) || `Course ${courseId}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              (ID: {courseId})
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              — {courseActivities.length} activit{courseActivities.length !== 1 ? 'ies' : 'y'}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>

                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      {courseActivities.map(activity => (
                        <ListItem
                          key={activity.activityId}
                          button
                          onClick={() => toggleActivity(activity)}
                          sx={{ pl: 8 }}
                        >
                          <ListItemIcon>
                            <Checkbox checked={selectedIds.has(activity.activityId)} />
                          </ListItemIcon>
                          <ListItemText
                            primary={activity.activityName}
                            secondary={`Tenant: ${activity.tenantId} | Course: ${activity.courseId}`}
                          />
                        </ListItem>
                      ))}
                    </Collapse>
                  </React.Fragment>
                );
              })}
            </List>
          </Paper>

          {selectedActivities.length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Please select at least one CMI5 activity to continue.
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};

// Step 3: Select S3 Environment
const S3EnvironmentSelectionStep: React.FC = () => {
  const wizard = useWizard();
  const [environments, setEnvironments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadEnvironments = async () => {
      try {
        const data = await basePrefixApi.getEnvironmentsWithBuckets();
        setEnvironments(data.map(env => ({
          id: env.environmentId,
          name: env.environmentName,
          basePrefix: env.prefix
        })));
      } catch (err: any) {
        wizard.setError(`Failed to load S3 environments: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    loadEnvironments();
  }, []);

  useEffect(() => {
    const environmentId = wizard.getData('s3EnvironmentId');
    const isValid = !!environmentId && environments.some(e => e.id === environmentId);
    // Calculate this step's index
    const contentTypes = wizard.getData<string[]>('contentTypes', []);
    let stepIndex = 2; // Start after Source=0, ContentTypes=1
    if (contentTypes.includes('categories')) stepIndex++; // Categories
    if (contentTypes.includes('courses')) {
      stepIndex++; // Courses
      stepIndex++; // Scenarios (always shown when courses selected)
    }
    if (contentTypes.includes('frameworks')) stepIndex++; // Frameworks
    if (contentTypes.includes('learning-paths')) stepIndex++; // Learning Paths
    // Now at S3 Environment position
    wizard.setStepValidation(stepIndex, isValid);
  }, [wizard.data, environments]);

  const handleEnvironmentChange = (envId: string) => {
    wizard.setData('s3EnvironmentId', envId);
    const env = environments.find(e => e.id === envId);
    wizard.setData('s3EnvironmentName', env?.name);
    wizard.setData('s3EnvironmentPrefix', env?.basePrefix); // Store the S3 prefix (folder name)
  };

  const handleEnvironmentCreated = (newEnv: { id: string; name: string; basePrefix: string }) => {
    // Add the new environment to the list
    setEnvironments(prev => [...prev, newEnv]);
    // Auto-select the newly created environment
    wizard.setData('s3EnvironmentId', newEnv.id);
    wizard.setData('s3EnvironmentName', newEnv.name);
    wizard.setData('s3EnvironmentPrefix', newEnv.basePrefix); // Store the S3 prefix
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <CreateEnvironmentInline
        onEnvironmentCreated={handleEnvironmentCreated}
        onError={(error) => wizard.setError(error)}
      />

      <FormControl fullWidth>
        <InputLabel>S3 Environment</InputLabel>
        <Select
          value={wizard.getData('s3EnvironmentId', '')}
          onChange={(e) => handleEnvironmentChange(e.target.value)}
          label="S3 Environment"
        >
          {environments.map(env => (
            <MenuItem key={env.id} value={env.id}>
              <Box>
                <Typography variant="body1">{env.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {env.basePrefix}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {environments.length === 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          No S3 environments configured. Please add an S3 environment in the Config section first.
        </Alert>
      )}

      {wizard.getData('s3EnvironmentId') && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Content will be backed up to: <strong>{wizard.getData('s3EnvironmentName')}</strong>
        </Alert>
      )}
    </Box>
  );
};

// Step 4: Configure Backup Options
const BackupConfigStep: React.FC = () => {
  const wizard = useWizard();
  const [versionDescription, setVersionDescription] = useState(wizard.getData('versionDescription', ''));
  const [tags, setTags] = useState(wizard.getData('tags', ''));
  const [createLinkages, setCreateLinkages] = useState(wizard.getData('createLinkages', true));

  useEffect(() => {
    wizard.setDataBulk({
      versionDescription,
      tags,
      createLinkages
    });
    // Calculate this step's index
    const contentTypes = wizard.getData<string[]>('contentTypes', []);
    let stepIndex = 2; // Start after Source=0, ContentTypes=1
    if (contentTypes.includes('categories')) stepIndex++; // Categories
    if (contentTypes.includes('courses')) {
      stepIndex++; // Courses
      stepIndex++; // Scenarios (always shown when courses selected)
    }
    if (contentTypes.includes('frameworks')) stepIndex++; // Frameworks
    if (contentTypes.includes('learning-paths')) stepIndex++; // Learning Paths
    stepIndex++; // S3 Environment
    // Now at Configure position
    wizard.setStepValidation(stepIndex, true); // This step is always valid
  }, [versionDescription, tags, createLinkages]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Backup Configuration
      </Typography>

      <TextField
        fullWidth
        label="Version Description"
        value={versionDescription}
        onChange={(e) => setVersionDescription(e.target.value)}
        multiline
        rows={3}
        placeholder="Describe this backup version..."
        sx={{ mb: 2 }}
        helperText="Optional: Add a description for this backup version"
      />

      <TextField
        fullWidth
        label="Tags"
        value={tags}
        onChange={(e) => setTags(e.target.value)}
        placeholder="production, stable, v2.0"
        sx={{ mb: 2 }}
        helperText="Optional: Add comma-separated tags"
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={createLinkages}
            onChange={(e) => setCreateLinkages(e.target.checked)}
          />
        }
        label="Create version linkages (tracks relationships between courses and activities)"
        sx={{ mb: 2 }}
      />

      <Alert severity="info">
        <Typography variant="body2">
          <strong>Note:</strong> Creating linkages allows you to maintain exact version relationships
          when importing content later. This is recommended for production backups.
        </Typography>
      </Alert>
    </Box>
  );
};

// Step 5: Review & Confirm
const ReviewConfirmStep: React.FC = () => {
  const wizard = useWizard();
  const contentTypes: string[] = wizard.getData('contentTypes', []);
  const selectedCourses: MoodleCourse[] = wizard.getData('selectedCourses', []);
  const selectedFrameworks: any[] = wizard.getData('selectedFrameworks', []);
  const selectedTemplates: any[] = wizard.getData('selectedTemplates', []);

  useEffect(() => {
    // Calculate this step's index
    const contentTypes = wizard.getData<string[]>('contentTypes', []);
    let stepIndex = 2; // Start after Source=0, ContentTypes=1
    if (contentTypes.includes('categories')) stepIndex++; // Categories
    if (contentTypes.includes('courses')) {
      stepIndex++; // Courses
      stepIndex++; // Scenarios (always shown when courses selected)
    }
    if (contentTypes.includes('frameworks')) stepIndex++; // Frameworks
    if (contentTypes.includes('learning-paths')) stepIndex++; // Learning Paths
    if (contentTypes.includes('cmi5')) stepIndex++; // CMI5 Activities
    stepIndex++; // S3 Environment
    stepIndex++; // Configure
    // Now at Review position
    wizard.setStepValidation(stepIndex, true);
  }, []);

  const selectedCMI5Activities: any[] = wizard.getData('selectedCMI5Activities', []);

  const sections: SummarySection[] = [
    {
      title: 'Backup Details',
      items: [
        { label: 'Source', value: wizard.getData('sourceName', 'Unknown'), highlight: true },
        { label: 'Destination', value: wizard.getData('s3EnvironmentName', 'Unknown'), highlight: true },
        { label: 'Content Types', value: contentTypes.length }
      ]
    },
    {
      title: 'Content Summary',
      items: [
        { label: 'Courses', value: contentTypes.includes('courses') ? selectedCourses.length : 'Not selected' },
        { label: 'Categories', value: contentTypes.includes('categories') ? 'Yes' : 'No' },
        { label: 'Frameworks', value: contentTypes.includes('frameworks') ? selectedFrameworks.length : 'Not selected' },
        { label: 'Learning Paths', value: contentTypes.includes('learning-paths') ? selectedTemplates.length : 'Not selected' },
        { label: 'CMI5 Activities', value: contentTypes.includes('cmi5') ? selectedCMI5Activities.length : 'Not selected' }
      ]
    },
    {
      title: 'Options',
      items: [
        { label: 'Version Description', value: wizard.getData('versionDescription') || 'None' },
        { label: 'Tags', value: wizard.getData('tags') || 'None' },
        { label: 'Create Linkages', value: wizard.getData('createLinkages') ? 'Yes' : 'No' }
      ]
    }
  ];

  const contentItems: ContentItem[] = [];

  if (contentTypes.includes('courses')) {
    contentItems.push(...selectedCourses.map(course => ({
      type: 'course' as const,
      name: course.fullname,
      id: course.shortname
    })));
  }

  if (contentTypes.includes('categories')) {
    contentItems.push({ type: 'category' as const, name: 'All Categories' });
  }

  if (contentTypes.includes('frameworks')) {
    contentItems.push(...selectedFrameworks.map(framework => ({
      type: 'framework' as const,
      name: framework.shortname,
      id: framework.idnumber
    })));
  }

  if (contentTypes.includes('learning-paths')) {
    contentItems.push(...selectedTemplates.map(template => ({
      type: 'framework' as const,
      name: template.name,
      id: template.idnumber || template.id.toString()
    })));
  }

  if (contentTypes.includes('cmi5')) {
    contentItems.push(...selectedCMI5Activities.map((activity: any) => ({
      type: 'activity' as const,
      name: activity.activityName,
      id: `${activity.tenantId}/${activity.courseId}`
    })));
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review Backup
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Please review the backup details below before proceeding.
      </Typography>

      <ProgressSummaryPanel sections={sections} />

      <Box sx={{ mt: 2 }}>
        <ContentPreviewPanel
          title="Content to Backup"
          items={contentItems}
        />
      </Box>

      <Alert severity="warning" sx={{ mt: 2 }}>
        <Typography variant="body2" fontWeight="bold">
          Important:
        </Typography>
        <Typography variant="body2">
          • The backup may take several minutes depending on content size
        </Typography>
        <Typography variant="body2">
          • Course backups will be saved to the S3 environment
        </Typography>
      </Alert>
    </Box>
  );
};

// Main Wizard Component
export const BackupMoodleToS3Wizard: React.FC<BackupMoodleToS3WizardProps> = ({
  open,
  onClose,
  onComplete
}) => {
  const [wizardSteps, setWizardSteps] = useState(() => buildWizardSteps([]));
  const backupCompleteHandlerRef = useRef<((data: Record<string, any>) => Promise<void>) | null>(null);

  // Wrapper function that calls the registered handler
  const handleComplete = useCallback(async (data: Record<string, any>) => {
    if (backupCompleteHandlerRef.current) {
      await backupCompleteHandlerRef.current(data);
    } else {
      console.error('Backup complete handler not registered');
    }
  }, []);

  return (
    <WizardDialog
      open={open}
      onClose={onClose}
      title="Backup Moodle to S3 Wizard"
      steps={wizardSteps}
      onComplete={handleComplete}
      width="lg"
      persistenceKey="backup-moodle-to-s3-wizard"
      showResumePrompt
    >
      <DynamicWizardContent
        onStepsChange={setWizardSteps}
        onComplete={onComplete}
        onHandlerReady={(handler) => { backupCompleteHandlerRef.current = handler; }}
      />
    </WizardDialog>
  );
};

// Inner component with completion handler
interface DynamicWizardContentProps {
  onStepsChange: (steps: any[]) => void;
  onComplete?: (jobId: string) => void;
  onHandlerReady: (handler: (data: Record<string, any>) => Promise<void>) => void;
}

const DynamicWizardContent: React.FC<DynamicWizardContentProps> = ({ onStepsChange, onComplete, onHandlerReady }) => {
  const { refreshJobs } = useJobs();
  const wizard = useWizard();

  // Update steps when content types change
  useEffect(() => {
    const contentTypes = wizard.getData<string[]>('contentTypes', []);
    const newSteps = buildWizardSteps(contentTypes);
    onStepsChange(newSteps);
  }, [wizard.data, onStepsChange]);

  const handleBackupComplete = useCallback(async (data: Record<string, any>) => {
    try {
      const sourceId = data.sourceId;
      const contentTypes: string[] = data.contentTypes || [];
      const selectedCourses: MoodleCourse[] = data.selectedCourses || [];

      // Use the environment ID (unique across all buckets)
      const targetEnv = data.s3EnvironmentId;

      // Track all started job IDs
      const jobIds: string[] = [];

      // Start export jobs based on selected content types
      // Categories should be exported first (if selected)
      if (contentTypes.includes('categories')) {
        try {
          console.log('Fetching all categories from Moodle...');
          const categories = await moodleApi.listCategories(sourceId);
          const categoryIds = categories.map(c => c.id);

          if (categoryIds.length > 0) {
            console.log(`Starting export of ${categoryIds.length} categories...`);

            // Start category export as background job
            await new Promise<void>((resolve) => {
              let jobId: string | undefined;
              let completeCalled = false;

              moodleApi.exportCategoriesWithProgress(
                sourceId,
                categoryIds,
                targetEnv,
                (progress) => {
                  console.log('Category export progress:', progress);
                },
                async (id) => {
                  jobId = id;
                  jobIds.push(id);
                  console.log('✅ Category export job created:', jobId);

                  if (!completeCalled) {
                    completeCalled = true;
                    resolve();
                  }
                }
              ).catch(err => {
                console.error('Category export failed:', err);
                if (!completeCalled) {
                  completeCalled = true;
                  resolve();
                }
              });

              // Timeout: If job ID not received within 2 seconds, continue anyway
              setTimeout(() => {
                if (!completeCalled) {
                  console.warn('Category job ID not received within timeout');
                  completeCalled = true;
                  resolve();
                }
              }, 2000);
            });
          } else {
            console.log('No categories found to export');
          }
        } catch (err) {
          console.error('Failed to export categories:', err);
        }
      }

      // Export courses (if selected)
      // Note: CMI5 activities are automatically exported with courses by the backend
      if (contentTypes.includes('courses') && selectedCourses.length > 0) {
        // Start export as background job with progress tracking
        return new Promise<void>((resolve) => {
          let jobId: string | undefined;
          let completeCalled = false;

          // Prepare scenario options
          const selectedScenarioUuids: Map<number, string[]> = data.selectedScenarioUuids || new Map();
          const scenarioOptions = selectedScenarioUuids.size > 0
            ? { includeScenarios: true, selectedScenarioUuids }
            : undefined;

          // Don't await - let it run in background
          moodleApi.exportCoursesWithProgress(
            sourceId,
            selectedCourses.map(c => c.id),
            targetEnv,
            (progress) => {
              // Progress updates are received via SSE - no need to handle here
              console.log('Export progress:', progress);
            },
            async (id) => {
              // Job created callback - notify immediately and close wizard
              jobId = id;
              jobIds.push(id);
              console.log('✅ Course export job created:', jobId);

              // Manually refresh jobs to ensure the new job appears in the Jobs Panel
              // This syncs the job with the SSE state via JobsContext.refreshJobs()
              try {
                await refreshJobs();
                console.log('✅ Jobs refreshed, new jobs should now be visible');
              } catch (err) {
                console.warn('Failed to refresh jobs after creation:', err);
              }

              // Now close the wizard - use the first course job ID
              if (!completeCalled && onComplete) {
                completeCalled = true;
                onComplete(jobId);
                resolve();
              }
            },
            scenarioOptions
          ).catch(err => {
            console.error('Export job failed:', err);
            if (!completeCalled) {
              resolve();
            }
          });

          // Timeout: If job ID not received within 2 seconds, close anyway
          setTimeout(() => {
            if (!completeCalled) {
              console.warn('Job ID not received within timeout, closing wizard anyway');
              completeCalled = true;
              if (onComplete) {
                onComplete(jobId || jobIds[0] || 'pending');
              }
              resolve();
            }
          }, 2000);
        });
      }

      // Export competency frameworks (if selected)
      if (contentTypes.includes('frameworks')) {
        try {
          const frameworkIds: number[] = data.selectedFrameworkIds || [];

          if (frameworkIds.length > 0) {
            console.log(`Starting export of ${frameworkIds.length} competency frameworks...`);

            await new Promise<void>((resolve) => {
              let jobId: string | undefined;
              let completeCalled = false;

              moodleApi.exportCompetencyFrameworksWithProgress(
                sourceId,
                frameworkIds,
                targetEnv,
                (progress) => {
                  console.log('Framework export progress:', progress);
                },
                async (id) => {
                  jobId = id;
                  jobIds.push(id);
                  console.log('✅ Framework export job created:', jobId);

                  if (!completeCalled) {
                    completeCalled = true;
                    resolve();
                  }
                }
              ).catch(err => {
                console.error('Framework export failed:', err);
                if (!completeCalled) {
                  completeCalled = true;
                  resolve();
                }
              });

              setTimeout(() => {
                if (!completeCalled) {
                  console.warn('Framework job ID not received within timeout');
                  completeCalled = true;
                  resolve();
                }
              }, 2000);
            });
          } else {
            console.log('No competency frameworks found to export');
          }
        } catch (err) {
          console.error('Failed to export competency frameworks:', err);
        }
      }

      // Export learning path templates (if selected)
      if (contentTypes.includes('learning-paths')) {
        try {
          const templateIds: number[] = data.selectedTemplateIds || [];

          if (templateIds.length > 0) {
            console.log(`Starting export of ${templateIds.length} learning path templates...`);

            await new Promise<void>((resolve) => {
              let jobId: string | undefined;
              let completeCalled = false;

              moodleApi.exportLearningPathTemplatesWithProgress(
                sourceId,
                templateIds,
                targetEnv,
                (progress) => {
                  console.log('Learning path export progress:', progress);
                },
                async (id) => {
                  jobId = id;
                  jobIds.push(id);
                  console.log('✅ Learning path export job created:', jobId);

                  if (!completeCalled) {
                    completeCalled = true;
                    resolve();
                  }
                }
              ).catch(err => {
                console.error('Learning path export failed:', err);
                if (!completeCalled) {
                  completeCalled = true;
                  resolve();
                }
              });

              setTimeout(() => {
                if (!completeCalled) {
                  console.warn('Learning path job ID not received within timeout');
                  completeCalled = true;
                  resolve();
                }
              }, 2000);
            });
          } else {
            console.log('No learning path templates found to export');
          }
        } catch (err) {
          console.error('Failed to export learning path templates:', err);
        }
      }

      // Handle standalone CMI5 activity export
      if (contentTypes.includes('cmi5')) {
        try {
          const selectedCMI5Activities = data.selectedCMI5Activities || [];
          if (selectedCMI5Activities.length > 0) {
            console.log(`Starting export of ${selectedCMI5Activities.length} CMI5 activities...`);
            await new Promise<void>((resolve) => {
              let completeCalled = false;
              moodleApi.exportCMI5ActivitiesWithProgress(
                sourceId,
                selectedCMI5Activities,
                targetEnv,
                (progress) => {
                  console.log('CMI5 export progress:', progress);
                },
                async (id) => {
                  jobIds.push(id);
                  console.log('✅ CMI5 export job created:', id);
                  try {
                    await refreshJobs();
                  } catch (err) {
                    console.warn('Failed to refresh jobs:', err);
                  }
                  if (!completeCalled) {
                    completeCalled = true;
                    resolve();
                  }
                }
              ).catch(err => {
                console.error('CMI5 export failed:', err);
                if (!completeCalled) {
                  completeCalled = true;
                  resolve();
                }
              });
              setTimeout(() => {
                if (!completeCalled) {
                  console.warn('CMI5 job ID not received within timeout');
                  completeCalled = true;
                  resolve();
                }
              }, 2000);
            });
          }
        } catch (err) {
          console.error('Failed to export CMI5 activities:', err);
        }
      }

      // If only non-course content was selected, close the wizard
      if (jobIds.length > 0 && !contentTypes.includes('courses')) {
        // Manually refresh jobs
        try {
          await refreshJobs();
          console.log('✅ Jobs refreshed');
        } catch (err) {
          console.warn('Failed to refresh jobs:', err);
        }

        if (onComplete) {
          onComplete(jobIds[0]);
        }
      }
    } catch (err: any) {
      throw new Error(`Failed to start backup: ${err.message}`);
    }
  }, [refreshJobs, onComplete]);

  // Register the handler with parent
  useEffect(() => {
    onHandlerReady(handleBackupComplete);
  }, [handleBackupComplete, onHandlerReady]);

  return <DynamicWizardSteps />;
};

// Component to render dynamic wizard steps based on content type selection
const DynamicWizardSteps: React.FC = () => {
  const wizard = useWizard();

  // Read data directly from wizard - this will re-render when wizard context updates
  const contentTypes = wizard.getData<string[]>('contentTypes', []);
  const scenarioUuids = wizard.getData<Map<number, string[]>>('selectedScenarioUuids', new Map());
  const hasScenarios = scenarioUuids.size > 0;

  // Calculate current step index for each dynamic step type
  const getStepIndex = (stepType: 'source' | 'content-types' | 'categories' | 'courses' | 'scenarios' | 'frameworks' | 'learning-paths' | 'cmi5' | 's3-env' | 'config' | 'review'): number => {
    let index = 0;

    if (stepType === 'source') return index;
    index++; // Source = 0

    if (stepType === 'content-types') return index;
    index++; // Content Types = 1

    if (contentTypes.includes('categories')) {
      if (stepType === 'categories') return index;
      index++; // Categories
    }

    if (contentTypes.includes('courses')) {
      if (stepType === 'courses') return index;
      index++; // Courses

      // Scenarios step is ALWAYS shown when courses are selected
      if (stepType === 'scenarios') return index;
      index++; // Scenarios
    }

    if (contentTypes.includes('frameworks')) {
      if (stepType === 'frameworks') return index;
      index++; // Frameworks
    }

    if (contentTypes.includes('learning-paths')) {
      if (stepType === 'learning-paths') return index;
      index++; // Learning Paths
    }

    if (contentTypes.includes('cmi5')) {
      if (stepType === 'cmi5') return index;
      index++; // CMI5 Activities
    }

    if (stepType === 's3-env') return index;
    index++; // S3 Environment

    if (stepType === 'config') return index;
    index++; // Config

    if (stepType === 'review') return index;
    return index; // Review
  };

  return (
    <>
      <WizardStep stepIndex={getStepIndex('source')}>
        <SourceSelectionStep />
      </WizardStep>

      <WizardStep stepIndex={getStepIndex('content-types')}>
        <ContentTypeSelectionStep />
      </WizardStep>

      {contentTypes.includes('categories') && (
        <WizardStep stepIndex={getStepIndex('categories')}>
          <CategoriesConfirmationStep />
        </WizardStep>
      )}

      {contentTypes.includes('courses') && (
        <>
          <WizardStep stepIndex={getStepIndex('courses')}>
            <CourseSelectionStep />
          </WizardStep>
          <WizardStep stepIndex={getStepIndex('scenarios')}>
            <ScenarioSelectionStep />
          </WizardStep>
        </>
      )}

      {contentTypes.includes('frameworks') && (
        <WizardStep stepIndex={getStepIndex('frameworks')}>
          <FrameworkSelectionStep />
        </WizardStep>
      )}

      {contentTypes.includes('learning-paths') && (
        <WizardStep stepIndex={getStepIndex('learning-paths')}>
          <LearningPathSelectionStep />
        </WizardStep>
      )}

      {contentTypes.includes('cmi5') && (
        <WizardStep stepIndex={getStepIndex('cmi5')}>
          <CMI5ActivitySelectionStep />
        </WizardStep>
      )}

      <WizardStep stepIndex={getStepIndex('s3-env')}>
        <S3EnvironmentSelectionStep />
      </WizardStep>

      <WizardStep stepIndex={getStepIndex('config')}>
        <BackupConfigStep />
      </WizardStep>

      <WizardStep stepIndex={getStepIndex('review')}>
        <ReviewConfirmStep />
      </WizardStep>
    </>
  );
};
